import React, { useRef, useImperativeHandle, useEffect } from 'react';
import { StyledInputWrapper, StyledInputLabel, StyledRequired, StyledOptional, StyledInputTextarea, StyledInputErrorMsg, GlobalStyles } from 'veronica-ui-component/dist/component/core/styled/inputTextarea.styled';
import { HPHRed } from 'veronica-ui-component/dist/component/core/Colors';
import { IconButton } from 'veronica-ui-component/dist/component/core/IconButtons/IconButtons';
import { Lozenges } from 'veronica-ui-component/dist/component/core/Lozenges/Lozenges';
import { HPHBadge } from 'veronica-ui-component/dist/component/core/Badge/Badge';
import { InputTextarea } from 'primereact/inputtextarea';
import { DynamicProp } from 'veronica-ui-component/dist/common/commonInterface';

export interface TextareaProps extends DynamicProp {
    element?: React.Ref<InputTextarea>;
    placeholder?: string | '';
    value?: string;
    label?: string;
    disabled?: boolean | false;
    helpIcon?: boolean | false;
    systemIndicator?: boolean;
    required?: boolean | false;
    optional?: boolean | false;
    errorMessage?: string;
    toolTipText?: string;
    helperMessage?: string;
    rows?: number;
    autoExpand?: boolean | false;
    cols?: number;
    size?: string;
    width?: string | '250px';
    className?: string | 'p-badge';
    onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
    resizable?: boolean;
    id?: string;
}

const NbisInputTextarea = React.forwardRef((props: TextareaProps, ref: React.Ref<any>) => {
    const {
        element,
        placeholder = '',
        className,
        label = 'label',
        disabled = false,
        helpIcon = false,
        systemIndicator = false,
        required = false,
        optional = false,
        errorMessage = '',
        toolTipText = 'Helper Message',
        rows = 4,
        cols = 30,
        autoExpand = false,
        width = '250px',
        value,
        onChange,
        resizable = false,
        id = 'textArea',
        ...rest
    } = props;

    const textareaRef = useRef<any>(null);
    const cursorPositionRef = useRef<number>(0);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const target = e.target as HTMLTextAreaElement;
        cursorPositionRef.current = target.selectionStart;

        onChange && onChange(e);
    };

    useImperativeHandle(ref, () => ({
        textareaRef: textareaRef,
    }));

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.selectionStart = cursorPositionRef.current;
            textareaRef.current.selectionEnd = cursorPositionRef.current;
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
    }, [value]);

    return (
        <StyledInputWrapper>
            <GlobalStyles />
            <StyledInputLabel>
                {label} {required && !optional && <StyledRequired>*</StyledRequired>}{optional && !required ? <StyledOptional><Lozenges variation={'Optional Label'} /></StyledOptional> : ''}
                {helpIcon && <IconButton idIcon={`Icon-help-${id}`} fileName={'Icon-help'} size={'small'}
                    toolTipPlacement={'right'} toolTipArrow={false} disabled={false}
                    toolTipText={toolTipText} {...rest} />}
                {systemIndicator && <HPHBadge value='s' color='Aqua-Green' />}
            </StyledInputLabel>
            <StyledInputTextarea
                placeholder={placeholder}
                rows={rows}
                cols={cols}
                disabled={disabled}
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                style={{ width, ...errorMessage !== '' ? { borderColor: HPHRed } : {} }}
                required={required}
                autoResize={autoExpand}
                resizable={resizable ? 1 : 0}
                autoComplete="off"
                {...rest}
            />
            <StyledInputErrorMsg>{errorMessage}</StyledInputErrorMsg>
        </StyledInputWrapper>
    );
});

export default NbisInputTextarea; 